import React, { useState, useEffect } from "react";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import axios from "axios";
import { Link, useNavigate } from 'react-router-dom';
import { Card, Button } from "react-bootstrap";
import { API_ENDPOINTS } from '../../config/api';

const eventDotColor = "#494d53"; // Bootstrap primary

// Event type colors (should match AddEventModal/EditEventModal)
const eventTypeColors = {
  'Clinic': '#aec6e4', // Blue
  'OR': '#ffb3ba',     // Red
  'Meeting': '#b2e0b2', // Green
  'Education': '#ffdfba', // Yellow
  'Journal Club': '#dbffba', // lime 
  'Personal': '#c3b1e1', // Purple
  'Vacation': '#c7eff7', // Blue
  'Sick': '#e9f4b1', // Yellow
  'Weekend Call': '#fab6a3', // Red
  'Other': '#b2e7e0', // Gray
  'default': '#aec6e4'  // Default blue
};

// Helper to format date as YYYY-MM-DD in local time
const formatDateLocal = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(1, '0');
  const day = String(date.getDate()).padStart(1, '0');
  return `${year}-${month}-${day}`;
};

const DashboardCalendarWidget = () => {
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAllEvents, setShowAllEvents] = useState(false);
  const isDirector = localStorage.getItem('role') === 'director';

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) return;

        const response = await axios.get(`${API_ENDPOINTS.EVENTS}/my-events`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const formattedEvents = response.data.map((event) => ({
          ...event,
          start: new Date(event.start),
          end: new Date(event.end),
        }));

        setEvents(formattedEvents);
      } catch (error) {
        console.error("Error fetching events:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, []);

  const eventsForSelectedDate = events.filter(
    (event) => {
      const eventStart = new Date(event.start);
      const eventEnd = new Date(event.end);
      const selectedDateStart = new Date(selectedDate);
      selectedDateStart.setHours(0, 0, 0, 0);
      const selectedDateEnd = new Date(selectedDate);
      selectedDateEnd.setHours(23, 59, 59, 999);

      // Check if the selected date falls within the event's duration
      return (
        (selectedDateStart >= eventStart && selectedDateStart <= eventEnd) ||
        (selectedDateEnd >= eventStart && selectedDateEnd <= eventEnd) ||
        (eventStart >= selectedDateStart && eventEnd <= selectedDateEnd)
      );
    }
  );

  return (
    <Card className="card shadow-sm border-0" style={{ minWidth: 280, maxWidth: 520, width: '100%' }}>
      <Card.Body className={isDirector ? "p-4" : "p-md-3"}>
        <div className="d-flex flex-column mb-4">
          <h6 className="fw-semibold mb-0">Event Calendar</h6>
          <p className="text-muted small mb-3">Your Event Calendar</p>
          <Calendar
            onChange={setSelectedDate}
            value={selectedDate}
            locale="en-US"
            formatShortWeekday={(locale, date) => date.toLocaleString(locale, { weekday: 'short' }).slice(0, 2)}
            // Customizing navigation
            prevLabel={<i className="bi bi-chevron-left"></i>} // Bootstrap icon for left arrow
            nextLabel={<i className="bi bi-chevron-right"></i>} // Bootstrap icon for right arrow
            prev2Label={null} // Hides the "previous year" arrow
            next2Label={null} // Hides the "next year" arrow
            navigationLabel={({ date, view }) => {
              if (view === 'month') {
                return (
                  <span className="react-calendar__navigation__label__custom">
                    {date.toLocaleString('default', { month: 'long', year: 'numeric' })}
                  </span>
                );
              }
              if (view === 'year') {
                return (
                  <span className="react-calendar__navigation__label__custom">
                    {date.toLocaleString('default', { year: 'numeric' })}
                  </span>
                );
              }
              if (view === 'decade') {
                const startYear = Math.floor(date.getFullYear() / 10) * 10;
                return (
                  <span className="react-calendar__navigation__label__custom">
                    {startYear} - {startYear + 9}
                  </span>
                );
              }
              return null;
            }}
            tileClassName={({ date, view }) => {
              if (events.some(e => e.start.toDateString() === date.toDateString())) {
                return 'react-calendar__tile--hasEvent';
              }
              return null;
            }}
            // Re-integrated your original tileContent for the dots
            tileContent={({ date, view }) => {
              const dateEvents = events.filter(e => {
                const eventDate = new Date(e.start);
                return eventDate.toDateString() === date.toDateString();
              });
              
              if (dateEvents.length > 0) {
                // Use the first event's color, or default if no label
                const firstEvent = dateEvents[0];
                const dotColor = eventTypeColors[firstEvent.label] || eventTypeColors.default;
                
                return (
                  <div style={{ display: 'flex', justifyContent: 'center', marginBottom:-5 }}>
                    <span style={{ width: 5, height: 5, borderRadius: '50%', background: dotColor, display: 'inline-block' }}></span>
                  </div>
                );
              }
              return null;
            }}
          />
        </div>

        <div className="mt-4 pt-3 border-top">
          <div className="d-flex flex-row align-items-center justify-content-between mb-3">
          <div>
            <h6 className="fw-semibold mb-0">Today's Schedule</h6>
            <p className="text-muted mb-0 small">
              {selectedDate.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
            </p>
          </div>
              <Button 
                variant="light-solid"
                size="sm"
                onClick={() => navigate('/calendar')}
              >
                <i className="bi bi-plus-lg"></i>
              </Button>
              </div>
          {loading ? (
            <div className="d-flex align-items-center justify-content-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : eventsForSelectedDate.length > 0 ? (
            <>
            <div className="d-flex flex-column gap-2">
              {(showAllEvents ? eventsForSelectedDate : eventsForSelectedDate.slice(0, 5)).map((event, index) => (
                <Link
                  key={event.id}
                  to={`/calendar?date=${formatDateLocal(event.start)}`}
                  style={{ textDecoration: 'none', color: 'inherit' }}
                >
                  <div
                    className="d-flex align-items-start gap-2 p-2 rounded"
                    style={{border: '1px dashed var(--border-bg)'}}
                    onMouseEnter={(e) => {
                       e.currentTarget.style.backgroundColor = '#f8f9fa';
                       e.currentTarget.style.border = '1px solid var(--border-bg)';
                    }}
                    onMouseLeave={(e) => {
                       e.currentTarget.style.backgroundColor = 'transparent';
                       e.currentTarget.style.border = '1px dashed var(--border-bg)';
                    }}
                  >
                    {/* Time Column (Left) */}
                    <div className="d-flex flex-column align-items-center" style={{ minWidth: '60px' }}>
                      <span className="fw-semibold small">
                        {event.allDay 
                          ? 'All Day' 
                          : event.start.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
                        }
                      </span>
                      <div 
                        className="mt-1"
                        style={{
                          width: '8px',
                          height: '8px',
                          borderRadius: '50%',
                          backgroundColor: eventTypeColors[event.label] || eventTypeColors.default,
                          flexShrink: 0
                        }}
                      />
                    </div>
                    
                    {/* Event Details Column (Right) */}
                    <div className="flex-fill">
                      <p className="fw-semibold small mb-1">
                        {event.title}
                      </p>
                      {(event.location || event.label) && (
                        <div className="d-flex align-items-center gap-2">
                          {event.location && (
                            <>
                              <i className="bi bi-geo-alt-fill text-muted" style={{ fontSize: '0.75rem', opacity: 0.7 }}></i>
                              <span className="text-muted small" style={{ fontSize: '0.75rem' }}>
                                {event.location}
                              </span>
                            </>
                          )}
                          {/*{event.location && event.label && (
                            <span className="text-muted" style={{ fontSize: '0.85rem' }}>•</span>
                          )}
                          {event.label && (
                            <span 
                              className="small px-2 py-1 rounded-pill"
                              style={{
                                backgroundColor: eventTypeColors[event.label] || eventTypeColors.default,
                                color: '#000',
                                fontSize: '0.75rem',
                                fontWeight: 'normal'
                              }}
                            >
                              {event.label}
                            </span>
                          )}*/}
                        </div>
                      )}
                      {/*{!event.allDay && event.end && (
                        <p className="text-muted small mb-0 mt-1" style={{ fontSize: '0.85rem' }}>
                          Until {event.end.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      )}*/}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
            {eventsForSelectedDate.length > 5 && (
              <div className="text-center mt-2">
                <button
                  className="btn btn-sm btn-outline-primary"
                  onClick={() => setShowAllEvents((prev) => !prev)}
                >
                  {showAllEvents ? 'Show less' : `Show all (${eventsForSelectedDate.length})`}
                </button>
              </div>
            )}
            </>
          ) : (
            <>
            <div className="text-center py-5">
              <img src="/images/undraw_date-picker_8qys.svg" alt="No events" className="img-fluid" style={{height: '100px'}} />
              <p className="text-muted  mb-0 mt-3">No events scheduled for this day.</p>
            </div>

            </>
          )}
        </div>
      </Card.Body>
      <style>{`
        .react-calendar {
          width: 100%;
          border: none;
          background-color: transparent !important;
          font-family: inherit;
        }

        /* Navigation Header */
        .react-calendar__navigation {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1.5rem;
          padding: 0;
        }

        /* Navigation Arrows - Light grey rounded squares */
        .react-calendar__navigation button:first-child,
        .react-calendar__navigation button:last-child {
          background-color: var(--gray-100) !important;
          border: none !important;
          border-radius: 6px !important;
          color: var(--gray-700) !important;
          padding: 0.5rem !important;
          width: 36px !important;
          height: 36px !important;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background-color 0.2s;
        }
          .react-calendar__navigation button:first-child i,
        .react-calendar__navigation button:last-child i{
          padding: 0.37rem  0.7rem !important;
          margin-left: 1px;
          border-radius: 3px !important;
          background-color: var(--gray-100) !important;
        }

        .react-calendar__navigation button:first-child:hover,
        .react-calendar__navigation button:last-child:hover {
          background-color: var(--gray-100)   !important;
        }
           .react-calendar__navigation button:first-child:hover i,
        .react-calendar__navigation button:last-child:hover i{
          padding: 0.37rem  0.7rem !important;
          margin-left: 1px;
          border-radius: 3px !important;
          background-color: var(--white-alt) !important;
        }

        .react-calendar__navigation button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        /* Month/Year Label - Centered, bold, dark grey */
        .react-calendar__navigation__label {
          flex-grow: 1;
          text-align: center;
          pointer-events: none;
        }

        .react-calendar__navigation__label__custom {
          font-size: 1rem !important;
          font-weight: 600 !important;
          color: var(--gray-800) !important;
          padding: 0;
        }

        /* Hide the default navigation label button */
        .react-calendar__navigation__label button {
          pointer-events: auto;
          background: none !important;
          border: none !important;
          padding: 0 !important;
        }

        /* Weekday Headers - Light grey, evenly spaced */
        .react-calendar__month-view__weekdays {

        }

        .react-calendar__month-view__weekdays__weekday {
          font-size: 0.875rem;
          font-weight: 600;
          color: var(--gray-700) !important;
          text-align: center;
          text-transform: none !important;
        }

        .react-calendar__month-view__weekdays__weekday abbr {
          text-decoration: none;
          border: none;
          cursor: default;
        }

        /* Date Grid */
        .react-calendar__month-view__days {
          gap: 0;
        }

        .react-calendar__month-view__days__day {
          padding: 0.75rem 0 !important;
          font-size: 0.95rem;
          color: var(--gray-800) !important;
          text-align: center;
          border-radius: 8px !important;
          transition: all 0.2s;
        }

        /* Previous/Next Month Dates - Grayed out */
        .react-calendar__month-view__days__day--neighboringMonth {
          color:var(--gray-500) !important;
        }

        /* Today - Keep subtle, not highlighted */
        .react-calendar__tile--now {
          background-color: transparent !important;
          color: var(--gray-800) !important;
          font-weight: 500;
        }

        /* Selected/Active Date - Dark black background, white text, rounded */
        .react-calendar__tile--active {
          background-color: var(--gray-800) !important;
          color: var(--white) !important;
          font-weight: 700 !important;
          border-radius: 8px !important;
        }

        .react-calendar__tile--active:enabled:hover,
        .react-calendar__tile--active:enabled:focus {
          background-color: var(--gray-800) !important;
          color: var(--white) !important;
        }

        /* Hover effect for dates */
        .react-calendar__tile:enabled:hover,
        .react-calendar__tile:enabled:focus {
          background-color: var(--gray-300) !important;
          border-radius: 8px !important;
          color: var(--gray-700) !important;
        }

        /* Don't override active state on hover */
        .react-calendar__tile--active:enabled:hover,
        .react-calendar__tile--active:enabled:focus {
          background-color: var(--gray-800) !important;
            color: var(--white) !important;
          }

        /* Event dots */
        .react-calendar__tile--hasEvent {
          position: relative;
        }

        .react-calendar__tile--hasEvent div span {
          width: 4px;
          height: 4px;
          border-radius: 50%;
          display: inline-block;
          margin-top: 2px;
        }

        /* Weekend dates - same as weekdays */
        .react-calendar__month-view__days__day--weekend:not(.react-calendar__tile--active) {
          color: var(--gray-500) !important;
        }

        .react-calendar__month-view__days__day--weekend.react-calendar__month-view__days__day--neighboringMonth {
          color: var(--gray-500) !important;
        }

        /* Remove default tile styling */
        .react-calendar__tile {
          background: none;
          border: none;
        }

        /* Year/Decade view adjustments */
        .react-calendar__year-view .react-calendar__tile {
          padding: 1rem 0.5rem !important;
        }

        /* Remove abbr tooltips */
        .react-calendar__month-view__weekdays__weekday abbr[title] {
          pointer-events: none;
        }
      `}</style>
    </Card>
  );
};

export default DashboardCalendarWidget;